/**
 * ClassificationReviewModal - Two-panel review for classification accuracy
 *
 * Left panel:  Record context (summary/verbatim toggle + customer thread)
 * Right panel: Applied labels per dimension + accuracy rating + comments
 *
 * Submits to POST /v1/feedback/classification/{id}
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { cn } from '../../../../lib/utils';
import { fetchWithAuth } from '../../../../utils/api';
import { toast } from 'sonner';
import IconWrapper from '../../../ui/IconWrapper';
import { FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { ConductScoreIndicator } from './ConductScoreIndicator';
import type {
  Record as ConductRecord,
  DynamicCategoryLabels,
  TaxonomyLabel,
} from '../../../../types/record';

type AccuracyValue = 'accurate' | 'partially_accurate' | 'inaccurate';
type HelpfulnessValue = 'helpful' | 'not_helpful';

interface ClassificationReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classificationId: string;
  record: ConductRecord;
  onFeedbackSubmitted?: () => void;
}

const ACCURACY_OPTIONS: { value: AccuracyValue; label: string; color: string }[] = [
  { value: 'accurate', label: 'Accurate', color: 'border-[var(--go-success)]/30 bg-[var(--go-success)]/10 text-[var(--go-success)]' },
  { value: 'partially_accurate', label: 'Partially', color: 'border-[var(--go-warning)]/30 bg-[var(--go-warning)]/10 text-[var(--go-warning)]' },
  { value: 'inaccurate', label: 'Inaccurate', color: 'border-[var(--go-bittersweet)]/30 bg-[var(--go-bittersweet)]/10 text-[var(--go-bittersweet)]' },
];

/** Format snake_case / kebab-case to Title Case */
const formatLabel = (raw: string): string => {
  if (!raw) return '';
  return raw
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (w.length === 0 ? '' : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
};

/** Extract flat label list from DynamicCategoryLabels */
const flattenDynamic = (cats: DynamicCategoryLabels): TaxonomyLabel[] =>
  Object.values(cats).flat();

/** Dimension colour mapping for label pills */
const DIMENSION_PILL_COLORS: Record<string, string> = {
  dissatisfaction: 'border-[var(--go-teal)]/30 text-[var(--go-teal)]',
  strain: 'border-[var(--go-purple)]/30 text-[var(--go-purple)]',
  harm: 'border-[var(--go-berry)]/30 text-[var(--go-berry)]',
  'consumer-duty': 'border-[var(--go-blue)]/30 text-[var(--go-blue)]',
  'root-causes': 'border-[var(--go-pink)]/30 text-[var(--go-pink)]',
};

/** Compact label pills for a single dimension */
function DimensionSection({
  title,
  labels,
  variant,
}: {
  title: string;
  labels: TaxonomyLabel[];
  variant: string;
}) {
  const pillColor = DIMENSION_PILL_COLORS[variant] ?? 'border-gray-300 text-gray-600';

  if (labels.length === 0) {
    return (
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1">{title}</p>
        <p className="text-xs text-muted-foreground/60 italic">None detected</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{title}</p>
      <div className="flex flex-wrap gap-1">
        {labels.map((l) => (
          <span
            key={l.id}
            className={cn(
              'inline-flex items-center rounded border bg-background px-2 py-0.5 text-xs font-medium',
              pillColor,
            )}
          >
            {l.name || formatLabel(l.id)}
          </span>
        ))}
      </div>
    </div>
  );
}

export function ClassificationReviewModal({
  open,
  onOpenChange,
  classificationId,
  record,
  onFeedbackSubmitted,
}: ClassificationReviewModalProps) {
  const [showVerbatim, setShowVerbatim] = useState(false);
  const [accuracy, setAccuracy] = useState<AccuracyValue>();
  const [helpfulness, setHelpfulness] = useState<HelpfulnessValue>();
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasAnyFeedback = accuracy || helpfulness || comment.trim().length > 0;

  const resetForm = () => {
    setAccuracy(undefined);
    setHelpfulness(undefined);
    setComment('');
    setShowVerbatim(false);
  };

  const handleSubmit = async () => {
    if (!hasAnyFeedback) {
      toast.error('Please provide a rating or comment');
      return;
    }

    setIsSubmitting(true);
    try {
      await fetchWithAuth(`/v1/feedback/classification/${classificationId}`, {
        method: 'POST',
        body: JSON.stringify({
          label_relevance: accuracy,
          overall_helpfulness: helpfulness,
          comment: comment || undefined,
        }),
      });

      toast.success('Feedback submitted. Thank you!');
      onFeedbackSubmitted?.();
      onOpenChange(false);
      resetForm();
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Flatten conduct labels for display
  const { conductLabels } = record;
  const dissatisfactionLabels = flattenDynamic(conductLabels.complaintDetection);
  const strainLabels = conductLabels.customerStrain.labels;
  const harmLabels = flattenDynamic(conductLabels.foreseeableHarm);
  const dutyLabels = flattenDynamic(conductLabels.consumerDuty);
  const rootCauseLabels = conductLabels.rootCauses.labels;
  const hasThread = record.customerThread && record.customerThread.messages.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-hidden p-0 sm:max-w-6xl">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>Review Classification</DialogTitle>
          <DialogDescription>
            Read the record context on the left, review the applied labels on the right.
          </DialogDescription>
        </DialogHeader>

        {/* Two-panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border min-h-0 flex-1 overflow-hidden">
          {/* ──── LEFT PANEL: Record context ──── */}
          <div className="flex flex-col min-h-0 overflow-y-auto px-6 py-4 max-h-[60vh] lg:max-h-[70vh]">
            {/* Header with score + summary */}
            <div className="flex items-start gap-3 mb-4">
              <ConductScoreIndicator
                score={record.conductScore}
                size="small"
                className="flex-shrink-0"
                breakdown={record.conductScoreBreakdown}
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground break-words">
                  {record.summary}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {record.customerName} &middot;{' '}
                  {new Date(record.date).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>

            {/* Summary / Verbatim toggle */}
            {(record.summaryDetailed || record.verbatim) && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-muted-foreground">
                    {showVerbatim ? 'Original Text' : 'Analysis Summary'}
                  </h4>
                  {record.summaryDetailed && record.verbatim && (
                    <button
                      type="button"
                      onClick={() => setShowVerbatim(!showVerbatim)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showVerbatim ? 'Show Summary' : 'Show Original'}
                    </button>
                  )}
                </div>
                {showVerbatim && record.verbatim ? (
                  <div
                    className="text-sm leading-relaxed text-foreground break-words whitespace-pre-wrap"
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    {record.verbatim}
                  </div>
                ) : record.summaryDetailed ? (
                  <div className="space-y-2">
                    {record.summaryDetailed.split('\n\n').map((paragraph, i) => (
                      <p
                        key={i}
                        className="text-sm leading-relaxed text-foreground break-words whitespace-pre-wrap"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {/* Customer Thread */}
            {hasThread && (
              <div className="border-t border-border pt-4">
                <h4 className="text-xs font-medium text-muted-foreground mb-3">
                  Customer Communication ({record.customerThread!.messageCount}{' '}
                  {record.customerThread!.messageCount === 1 ? 'message' : 'messages'})
                </h4>
                <div className="flex flex-col gap-3">
                  {record.customerThread!.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={cn(
                        'flex gap-2 max-w-[90%]',
                        msg.isCustomer ? 'self-start' : 'self-end flex-row-reverse',
                      )}
                    >
                      {/* Avatar */}
                      <div
                        className={cn(
                          'size-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium',
                          msg.isCustomer
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'bg-muted text-muted-foreground border border-border',
                        )}
                      >
                        {msg.authorInitials}
                      </div>

                      {/* Message */}
                      <div
                        className={cn(
                          'flex flex-col gap-0.5 min-w-0',
                          msg.isCustomer ? 'items-start' : 'items-end',
                        )}
                      >
                        <div
                          className={cn(
                            'flex items-center gap-1.5 flex-wrap',
                            msg.isCustomer ? 'flex-row' : 'flex-row-reverse',
                          )}
                        >
                          <span className="text-xs font-medium text-foreground">{msg.author}</span>
                          <span className="text-[10px] text-muted-foreground">{msg.timestamp}</span>
                        </div>
                        <div
                          className={cn(
                            'rounded-xl px-3 py-2 max-w-full',
                            msg.isCustomer
                              ? 'bg-primary/5 border border-primary/10 rounded-tl-sm'
                              : 'bg-muted/80 border border-border rounded-tr-sm',
                          )}
                        >
                          <p
                            className="text-xs leading-relaxed text-foreground break-words whitespace-pre-wrap"
                            style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                          >
                            {msg.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ──── RIGHT PANEL: Labels + Feedback ──── */}
          <div className="flex flex-col min-h-0 overflow-y-auto px-6 py-4 max-h-[60vh] lg:max-h-[70vh]">
            {/* Applied labels */}
            <div className="mb-5 space-y-3 rounded-2xl border border-border/70 bg-[var(--go-spring)]/70 p-4">
              <h4 className="text-sm font-semibold text-foreground">Applied Labels</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DimensionSection title="Dissatisfaction" labels={dissatisfactionLabels} variant="dissatisfaction" />
                <DimensionSection title="Customer Strain" labels={strainLabels} variant="strain" />
                <DimensionSection title="Foreseeable Harm" labels={harmLabels} variant="harm" />
                <DimensionSection title="Consumer Duty" labels={dutyLabels} variant="consumer-duty" />
              </div>
              {rootCauseLabels.length > 0 && (
                <div className="border-t border-border/70 pt-3">
                  <DimensionSection title="Root Causes" labels={rootCauseLabels} variant="root-causes" />
                </div>
              )}
            </div>

            {/* Overall accuracy */}
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-foreground">How accurate is this classification?</h4>
              <div className="grid grid-cols-3 gap-2">
                {ACCURACY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAccuracy(opt.value)}
                    className={cn(
                      'rounded-md border-2 px-3 py-2 text-center text-sm font-medium transition-all',
                      accuracy === opt.value ? opt.color : 'border-border hover:bg-muted',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Helpfulness */}
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-foreground">Was this classification helpful?</h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setHelpfulness('helpful')}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md border-2 px-3 py-2 text-sm transition-all',
                    helpfulness === 'helpful'
                      ? 'border-[var(--go-success)]/30 bg-[var(--go-success)]/10 text-[var(--go-success)]'
                      : 'border-border hover:border-[var(--go-success)]/40',
                  )}
                >
                  <IconWrapper icon={FiThumbsUp} className="size-4" />
                  Helpful
                </button>
                <button
                  type="button"
                  onClick={() => setHelpfulness('not_helpful')}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md border-2 px-3 py-2 text-sm transition-all',
                    helpfulness === 'not_helpful'
                      ? 'border-[var(--go-bittersweet)]/30 bg-[var(--go-bittersweet)]/10 text-[var(--go-bittersweet)]'
                      : 'border-border hover:border-[var(--go-bittersweet)]/40',
                  )}
                >
                  <IconWrapper icon={FiThumbsDown} className="size-4" />
                  Not Helpful
                </button>
              </div>
            </div>

            {/* Comments */}
            <div className="space-y-1.5 mb-4">
              <label htmlFor="review-comment" className="text-sm font-medium text-foreground">
                Comments
              </label>
              <textarea
                id="review-comment"
                rows={3}
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-ring"
                placeholder="What labels are wrong, missing, or could be improved?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 border-t border-border/70 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !hasAnyFeedback}>
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
